import { useEffect } from "react";
import { MapPin, ExternalLink, ChevronDown, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import CountdownGlass from "@/components/CountdownGlass";

// Asset URLs - Served locally to avoid CDN cross-origin issues
const ASSETS = {
  monumentImage: "/images/reloj-solar-hero.jpeg",
};

// Google Maps URL with direct coordinates - opens in new tab
const GOOGLE_MAPS_URL = "https://www.google.com/maps/place/41%C2%B038'16.0%22N+0%C2%B053'53.9%22W/@41.637778,-0.898302,17z";

const Home = () => {
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://studio.pickaxe.co/api/embed/bundle.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section with Sunrise Gradient Background */}
      <section 
        className="relative min-h-screen overflow-hidden"
        data-testid="hero-section"
      >
        {/* Sunrise Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-amber-50 via-orange-50/30 to-white" />
        
        {/* Subtle Pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23013E7F' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Main Content */}
        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-8">
          {/* Simple Image Container - No 3D effects */}
          <div className="mb-8 md:mb-12 animate-fade-in-up">
            <div className="relative">
              {/* Main image - clean, no effects */}
              <div className="relative w-80 h-64 sm:w-96 sm:h-72 md:w-[450px] md:h-[340px] rounded-2xl overflow-hidden shadow-xl">
                <img
                  src={ASSETS.monumentImage}
                  alt="Reloj Solar de Acero Inoxidable - Rotary Zaragoza"
                  className="w-full h-full object-cover"
                  data-testid="hero-clock-image"
                />
              </div>
              
              {/* Gold accent corners */}
              <div className="absolute -top-2 -left-2 w-10 h-10 border-t-4 border-l-4 border-[#F7A81B] rounded-tl-lg" />
              <div className="absolute -bottom-2 -right-2 w-10 h-10 border-b-4 border-r-4 border-[#F7A81B] rounded-br-lg" />
            </div>
          </div>

          {/* Title Section */}
          <div className="text-center animate-fade-in-up delay-200">
            <h1 
              className="font-bold-sans text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#013E7F] mb-4 tracking-tight"
              data-testid="hero-title"
            >
              Reloj Solar Rotary
            </h1>
            <p className="font-heading text-xl sm:text-2xl text-slate-600 mb-2">
              Zaragoza
            </p>
            <p className="font-body text-base md:text-lg text-slate-500 max-w-xl mx-auto mb-8">
              Arte y Ciencia en la Red de Relojes del Eclipse
            </p>
            
            {/* Centenario Badge */}
            <div className="inline-flex items-center gap-2 glass-panel px-5 py-2.5 rounded-full mb-8">
              <Sparkles className="w-4 h-4 text-[#F7A81B]" />
              <span className="text-sm font-heading text-[#013E7F] tracking-wider uppercase">
                Centenario 1925 - 2025
              </span>
            </div>
          </div>

          {/* CTA Button */}
          <div className="animate-fade-in-up delay-300">
            <Button 
              className="btn-primary text-base md:text-lg px-10 py-6 rounded-xl group shadow-lg"
              size="lg"
              data-testid="locate-monument-btn"
              onClick={() => {
                // Force open in new browser tab - never in iframe
                window.open(GOOGLE_MAPS_URL, '_blank');
              }}
            >
              <MapPin className="mr-2 h-5 w-5 group-hover:animate-bounce" />
              Localizar Monumento
              <ExternalLink className="ml-2 h-4 w-4 opacity-70" />
            </Button>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-fade-in-up delay-500">
            <div className="flex flex-col items-center gap-2 text-slate-400">
              <span className="text-xs uppercase tracking-widest font-heading">
                Eclipse 2026
              </span>
              <ChevronDown className="w-5 h-5 animate-bounce" />
            </div>
          </div>
        </div>
      </section>

      {/* Countdown Section with Glassmorphism */}
      <CountdownGlass />

      {/* Quick Info Section */}
      <section className="py-16 md:py-24 px-4 bg-gradient-to-b from-white to-slate-50" data-testid="quick-info-section">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {/* Card 1 - El Monumento */}
            <div className="card-glass p-6 md:p-8 rounded-2xl text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#013E7F]/10 rounded-2xl flex items-center justify-center">
                <span className="font-accent text-2xl text-[#013E7F]">2.5m</span>
              </div>
              <h3 className="font-heading text-lg font-semibold text-slate-800 mb-2">
                Acero Inoxidable
              </h3>
              <p className="text-slate-600 text-sm">
                Un reloj solar de 2.5 metros que marca horas, solsticios y equinoccios
              </p>
            </div>

            {/* Card 2 - Centenario */}
            <div className="card-glass p-6 md:p-8 rounded-2xl text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#F7A81B]/10 rounded-2xl flex items-center justify-center">
                <span className="font-accent text-2xl text-[#F7A81B]">100</span>
              </div>
              <h3 className="font-heading text-lg font-semibold text-slate-800 mb-2">
                Años de Historia
              </h3>
              <p className="text-slate-600 text-sm">
                Celebrando el centenario del Rotary Club de Zaragoza (1925-2025)
              </p>
            </div>

            {/* Card 3 - Eclipse */}
            <div className="card-glass p-6 md:p-8 rounded-2xl text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-slate-900/10 rounded-2xl flex items-center justify-center">
                <span className="font-accent text-2xl text-slate-800">2026</span>
              </div>
              <h3 className="font-heading text-lg font-semibold text-slate-800 mb-2">
                Eclipse Solar Total
              </h3>
              <p className="text-slate-600 text-sm">
                Zaragoza será testigo del eclipse solar total el 12 de agosto de 2026
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Lema Section */}
      <section className="py-12 md:py-16 px-4 bg-rotary-gradient" data-testid="lema-section">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-accent text-xl md:text-2xl text-white/90 italic">
            "Dar de sí antes de pensar en sí"
          </p>
          <p className="text-white/60 text-sm mt-4 font-heading uppercase tracking-widest">
            Lema Rotario
          </p>
        </div>
      </section>

      {/* Chatbot Asistente */}
      <section className="py-12 md:py-16 px-4 bg-white" data-testid="chatbot-section">
        <div className="max-w-3xl mx-auto text-center mb-8">
          <div className="divider-gold mb-6" />
          <h2 className="font-heading text-2xl md:text-3xl text-slate-800 mb-3">
            ¿Tienes alguna pregunta?
          </h2>
          <p className="text-slate-500 text-base">
            Nuestro asistente virtual puede ayudarte con cualquier consulta sobre el Reloj Solar y el Rotary Club de Zaragoza.
          </p>
        </div>
        <div className="max-w-3xl mx-auto">
          <div id="deployment-bf289c71-fae2-4789-bd20-e3e18e84d8bb"></div>
        </div>
      </section>
    </div>
  );
};

export default Home;
